import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Package, Clock, CheckCircle2, XCircle, Heart, TrendingUp, Download, Search, Filter,
  Eye, EyeOff, Edit3, Trash2, Merge, Flag, Archive, Image as ImageIcon, X, ArrowRight,
  AlertCircle, Store, Truck, ShoppingCart, Sparkles,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { api, formatApiError, API_BASE } from "@/lib/apiClient";

const CATEGORIES = [
  "Imported Drinks", "Chocolates & Sweets", "Chips & Snacks", "Instant Foods",
  "Biscuits & Bakery", "Ice Cream & Frozen Treats", "Novelty Products", "Other Products",
];
const PUBLIC_STATUSES = ["gathering_interest", "under_consideration", "sourcing", "ordered", "available_in_store", "currently_unavailable"];
const REJECTION_REASONS = [
  ["spam_or_irrelevant", "Spam or irrelevant"],
  ["inappropriate", "Inappropriate content"],
  ["cannot_identify", "Cannot identify product"],
  ["duplicate", "Duplicate request"],
  ["not_suitable", "Not suitable for store"],
  ["other", "Other"],
];
const COLORS = ["#FEED02", "#111111", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#64748B"];

export default function AdminWishlist() {
  const [tab, setTab] = useState("overview"); // overview | requests | products
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [products, setProducts] = useState([]);
  const [reqFilter, setReqFilter] = useState({ status_f: "pending_review", q: "", category: "" });
  const [prodFilter, setProdFilter] = useState({ status_f: "", visibility: "", q: "" });
  const [openRequest, setOpenRequest] = useState(null);
  const [openProduct, setOpenProduct] = useState(null);

  const loadStats = async () => { try { const { data } = await api.get("/admin/wishlist/stats"); setStats(data); } catch (e) { toast.error(formatApiError(e)); } };
  const loadRequests = async () => {
    try {
      const params = {};
      if (reqFilter.status_f) params.status_f = reqFilter.status_f;
      if (reqFilter.q) params.q = reqFilter.q;
      if (reqFilter.category) params.category = reqFilter.category;
      const { data } = await api.get("/admin/wishlist/requests", { params });
      setRequests(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadProducts = async () => {
    try {
      const params = {};
      if (prodFilter.status_f) params.status_f = prodFilter.status_f;
      if (prodFilter.visibility) params.visibility = prodFilter.visibility;
      if (prodFilter.q) params.q = prodFilter.q;
      const { data } = await api.get("/admin/wishlist/products", { params });
      setProducts(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { if (tab === "requests") loadRequests(); /* eslint-disable-next-line */ }, [tab, reqFilter.status_f, reqFilter.category]);
  useEffect(() => { if (tab === "products") loadProducts(); /* eslint-disable-next-line */ }, [tab, prodFilter.status_f, prodFilter.visibility]);

  const exportCsv = () => {
    const token = localStorage.getItem("pp_admin_token");
    window.open(`${API_BASE}/admin/wishlist/export.csv?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  return (
    <div data-testid="admin-wishlist" className="pb-16">
      <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Pop Panda · Community Discovery</div>
          <h1 className="editorial-h1 text-4xl">Product Requests & Wishlist</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="btn-pill btn-ghost text-xs" data-testid="btn-export-csv"><Download size={11} /> Export CSV</button>
          <button onClick={() => { loadStats(); if (tab === "requests") loadRequests(); if (tab === "products") loadProducts(); }} className="btn-pill btn-ghost text-xs">Refresh</button>
        </div>
      </header>

      <div className="flex gap-2 mb-6 border-b border-brand-line">
        {[["overview", "Overview"], ["requests", "Moderation Queue"], ["products", "All Products"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm border-b-2 transition ${tab === k ? "border-brand-charcoal font-medium" : "border-transparent text-brand-charcoal/55 hover:text-brand-charcoal"}`} data-testid={`tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && stats && <OverviewTab stats={stats} />}

      {tab === "requests" && (
        <>
          <FiltersBar>
            <select value={reqFilter.status_f} onChange={(e) => setReqFilter({ ...reqFilter, status_f: e.target.value })} className="pp-input w-auto" data-testid="filter-status">
              <option value="">All Statuses</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select value={reqFilter.category} onChange={(e) => setReqFilter({ ...reqFilter, category: e.target.value })} className="pp-input w-auto" data-testid="filter-category">
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <SearchBox value={reqFilter.q} onChange={(v) => setReqFilter({ ...reqFilter, q: v })} onEnter={loadRequests} />
          </FiltersBar>
          <RequestsTable rows={requests} onOpen={setOpenRequest} />
        </>
      )}

      {tab === "products" && (
        <>
          <FiltersBar>
            <select value={prodFilter.status_f} onChange={(e) => setProdFilter({ ...prodFilter, status_f: e.target.value })} className="pp-input w-auto" data-testid="filter-public-status">
              <option value="">All Public Statuses</option>
              {PUBLIC_STATUSES.map((s) => <option key={s} value={s}>{labelFor(s)}</option>)}
            </select>
            <select value={prodFilter.visibility} onChange={(e) => setProdFilter({ ...prodFilter, visibility: e.target.value })} className="pp-input w-auto">
              <option value="">All Visibility</option>
              <option value="public">Public</option>
              <option value="hidden">Hidden</option>
              <option value="archived">Archived</option>
            </select>
            <SearchBox value={prodFilter.q} onChange={(v) => setProdFilter({ ...prodFilter, q: v })} onEnter={loadProducts} />
          </FiltersBar>
          <ProductsTable rows={products} onOpen={setOpenProduct} />
        </>
      )}

      {openRequest && <RequestModal rid={openRequest} onClose={() => setOpenRequest(null)} onDone={() => { setOpenRequest(null); loadRequests(); loadStats(); }} />}
      {openProduct && <ProductModal pid={openProduct} onClose={() => setOpenProduct(null)} onDone={() => { setOpenProduct(null); loadProducts(); loadStats(); }} allProducts={products} />}
    </div>
  );
}

/* ===================== Overview ===================== */
function OverviewTab({ stats }) {
  const { kpis, by_category, over_time, top_requested, top_liked } = stats;
  const tiles = [
    ["Total Requests", kpis.total_requests, TrendingUp, "text-brand-charcoal"],
    ["Pending Review", kpis.pending, Clock, "text-amber-600"],
    ["Approved Products", kpis.approved_products, CheckCircle2, "text-emerald-600"],
    ["Total Likes", kpis.total_likes, Heart, "text-red-500"],
    ["Sourcing", kpis.sourcing, Truck, "text-blue-600"],
    ["Ordered", kpis.ordered, ShoppingCart, "text-purple-600"],
    ["Available in Store", kpis.available, Store, "text-emerald-700"],
    ["Rejected/Spam", kpis.rejected, XCircle, "text-slate-500"],
  ];
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {tiles.map(([l, v, Icn, c]) => (
          <div key={l} className="rounded-2xl bg-white border border-brand-line p-4">
            <div className={`text-[10px] uppercase tracking-widest ${c} inline-flex items-center gap-1`}><Icn size={11} /> {l}</div>
            <div className="font-display text-3xl mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-brand-line p-4 mb-6">
        <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-3">Request-to-Stock Conversion</div>
        <div className="font-display text-5xl">{kpis.conversion_pct}%</div>
        <div className="text-xs text-brand-charcoal/55 mt-1">{kpis.available} of {kpis.approved_products} approved products are available in store</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <ChartCard title="Requests over last 14 days">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={over_time}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#111111" strokeWidth={2} dot={{ fill: "#FEED02", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Requests by category">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={by_category} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={75} label={{ fontSize: 10 }}>
                {by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Most requested">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={top_requested.slice(0, 8)} layout="vertical" margin={{ left: 90 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
              <Tooltip />
              <Bar dataKey="request_count" fill="#FEED02" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Most liked">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={top_liked.slice(0, 8)} layout="vertical" margin={{ left: 90 }}>
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
              <Tooltip />
              <Bar dataKey="like_count" fill="#111111" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}

function ChartCard({ title, children }) { return (<div className="rounded-2xl bg-white border border-brand-line p-4"><div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-3">{title}</div>{children}</div>); }
function FiltersBar({ children }) { return (<div className="bg-white rounded-2xl border border-brand-line p-3 mb-4 flex flex-wrap gap-2 items-center">{children}</div>); }
function SearchBox({ value, onChange, onEnter }) { return (<div className="flex-1 min-w-[200px] relative"><Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/40" /><input value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onEnter()} placeholder="Search…" className="pp-input pl-8" data-testid="filter-search" /></div>); }

function labelFor(s) { return s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()); }

/* ===================== Requests Table ===================== */
function RequestsTable({ rows, onOpen }) {
  if (rows.length === 0) return <div className="bg-white rounded-2xl border border-brand-line p-10 text-center text-brand-charcoal/50 text-sm">No requests match your filters.</div>;
  return (
    <div className="bg-white rounded-2xl border border-brand-line overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 border-b border-brand-line">
            <tr>
              <th className="text-left p-3">Ref</th><th className="text-left p-3">Product</th>
              <th className="text-left p-3">Category</th><th className="text-left p-3">Status</th>
              <th className="text-left p-3">Submitted</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-brand-line/60 hover:bg-brand-muted/30 cursor-pointer" onClick={() => onOpen(r.id)} data-testid={`req-row-${r.public_ref}`}>
                <td className="p-3 font-mono text-xs">{r.public_ref}<div className="text-[10px] text-brand-gold font-bold">{r.tracking_code}</div></td>
                <td className="p-3">
                  <div className="font-medium truncate max-w-[220px]">{r.submitted_name}</div>
                  {r.submitted_brand && <div className="text-[11px] text-brand-charcoal/55">{r.submitted_brand}</div>}
                  {r.possible_duplicate_of && <div className="text-[10px] text-amber-700 mt-0.5 inline-flex items-center gap-1"><AlertCircle size={9} /> Possible duplicate</div>}
                  {r.canonical_product && <div className="text-[10px] text-emerald-700 mt-0.5">→ linked to {r.canonical_product.name}</div>}
                </td>
                <td className="p-3 text-xs">{r.submitted_category}</td>
                <td className="p-3"><StatusChip status={r.status} /></td>
                <td className="p-3 text-xs text-brand-charcoal/60">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right"><button className="text-xs text-brand-charcoal hover:underline">Open <ArrowRight size={10} className="inline" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    pending_review: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800", under_review: "bg-sky-100 text-sky-800",
    sourcing: "bg-blue-100 text-blue-800", ordered: "bg-purple-100 text-purple-800",
    available_in_store: "bg-emerald-200 text-emerald-900", cancelled: "bg-slate-200 text-slate-700",
    not_available: "bg-slate-200 text-slate-700",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${map[status] || "bg-slate-100 text-slate-700"}`}>{labelFor(status)}</span>;
}

/* ===================== Products Table ===================== */
function ProductsTable({ rows, onOpen }) {
  if (rows.length === 0) return <div className="bg-white rounded-2xl border border-brand-line p-10 text-center text-brand-charcoal/50 text-sm">No products yet. Approve requests to publish them.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {rows.map((p) => (
        <div key={p.id} className="bg-white rounded-2xl border border-brand-line overflow-hidden hover:shadow-md transition cursor-pointer" onClick={() => onOpen(p.id)} data-testid={`prod-card-${p.slug}`}>
          <div className="aspect-[4/3] bg-[#FEED02]/10 grid place-items-center overflow-hidden">
            {p.image_path ? <img src={`${API_BASE}/wishlist/images/${p.image_path}`} alt={p.name} className="w-full h-full object-cover" /> : <Package size={34} className="text-brand-charcoal/25" />}
          </div>
          <div className="p-4">
            <div className="font-display text-lg truncate">{p.name}</div>
            <div className="text-xs text-brand-charcoal/55">{p.brand}{p.brand && p.category ? " · " : ""}{p.category}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatusChip status={p.public_status} />
              {p.visibility !== "public" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">{p.visibility}</span>}
            </div>
            <div className="mt-3 pt-3 border-t border-brand-line flex items-center justify-between text-xs text-brand-charcoal/70">
              <span className="inline-flex items-center gap-1"><TrendingUp size={11} /> {p.request_count}</span>
              <span className="inline-flex items-center gap-1"><Heart size={11} /> {p.like_count}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===================== Request Modal ===================== */
function RequestModal({ rid, onClose, onDone }) {
  const [req, setReq] = useState(null);
  const [showReject, setShowReject] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/admin/wishlist/requests/${rid}`); setReq(data); setEditForm({ name: data.submitted_name, brand: data.submitted_brand, category: data.submitted_category, variant: data.submitted_variant, size: data.submitted_size, description: data.submitted_description, public_status: "gathering_interest" }); }
      catch (e) { toast.error(formatApiError(e)); onClose(); }
    })();
    // eslint-disable-next-line
  }, [rid]);
  if (!req) return null;

  const approve = async () => {
    setSaving(true);
    try {
      const body = { ...editForm };
      if (mergeTarget) body.merge_into_product_id = mergeTarget;
      await api.post(`/admin/wishlist/requests/${rid}/approve`, body);
      toast.success(mergeTarget ? "Merged into existing product" : "Approved & published");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell title={req.public_ref} subtitle={`Tracker: ${req.tracking_code || "—"} · Request review`} onClose={onClose} testid="request-modal">
      {req.image_path && <img src={`${API_BASE}/wishlist/images/${req.image_path}`} alt="" className="w-full max-h-56 object-cover rounded-xl mb-4 border border-brand-line" />}
      {req.wants_updates && req.customer_mobile && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 mb-4 text-sm">
          <div className="font-medium text-emerald-800 mb-0.5">Customer opted-in to updates</div>
          <div className="font-mono text-emerald-900">+91 {req.customer_mobile}</div>
          <div className="text-[11px] text-emerald-700/80 mt-1">Use only to notify when the product becomes available.</div>
        </div>
      )}
      {req.possible_duplicate_of && req.canonical_product && (
        <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 mb-4 text-sm text-amber-800">
          <div className="font-medium mb-1 flex items-center gap-1"><AlertCircle size={13} /> Possible duplicate detected</div>
          <div>This submission looks similar to <strong>{req.canonical_product.name}</strong>. You can merge it or approve as a new product.</div>
          <button onClick={() => setMergeTarget(req.canonical_product.id)} className={`btn-pill text-[11px] mt-2 ${mergeTarget ? "bg-amber-200 text-amber-900" : "btn-ghost"}`} data-testid="merge-suggested">{mergeTarget ? "✓ Will merge" : "Merge into this product"}</button>
        </div>
      )}
      {!showReject ? (
        <>
          <div className="space-y-3">
            <Field label="Product name"><input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="pp-input" data-testid="approve-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Brand"><input value={editForm.brand || ""} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} className="pp-input" /></Field>
              <Field label="Category">
                <select value={editForm.category || ""} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="pp-input">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Variant"><input value={editForm.variant || ""} onChange={(e) => setEditForm({ ...editForm, variant: e.target.value })} className="pp-input" /></Field>
              <Field label="Size"><input value={editForm.size || ""} onChange={(e) => setEditForm({ ...editForm, size: e.target.value })} className="pp-input" /></Field>
            </div>
            <Field label="Description"><textarea rows={2} value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="pp-input" /></Field>
            <Field label="Initial public status">
              <select value={editForm.public_status || "gathering_interest"} onChange={(e) => setEditForm({ ...editForm, public_status: e.target.value })} className="pp-input" data-testid="approve-public-status">
                {PUBLIC_STATUSES.map((s) => <option key={s} value={s}>{labelFor(s)}</option>)}
              </select>
            </Field>
            {(req.expected_price || req.quantity) && (
              <div className="text-[11px] text-brand-charcoal/55">Customer indicated: {req.expected_price ? `~₹${req.expected_price}` : ""}{req.expected_price && req.quantity ? " · " : ""}{req.quantity ? `${req.quantity} units` : ""}</div>
            )}
          </div>
          <div className="pt-4 flex justify-between gap-2 flex-wrap">
            <button onClick={() => setShowReject(true)} className="btn-pill text-xs bg-red-50 text-red-700 hover:bg-red-100" data-testid="btn-reject"><XCircle size={11} /> Reject</button>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-pill btn-ghost text-xs">Cancel</button>
              <button onClick={approve} disabled={saving} className="btn-pill btn-dark text-xs disabled:opacity-50" data-testid="btn-approve"><CheckCircle2 size={11} /> {mergeTarget ? "Merge & Approve" : "Approve & Publish"}</button>
            </div>
          </div>
        </>
      ) : (
        <RejectForm rid={rid} onBack={() => setShowReject(false)} onDone={onDone} />
      )}
    </ModalShell>
  );
}

function RejectForm({ rid, onBack, onDone }) {
  const [reason, setReason] = useState(REJECTION_REASONS[0][0]);
  const [message, setMessage] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/wishlist/requests/${rid}/reject`, { reason, customer_update: message, admin_notes: notes });
      toast.success("Request rejected"); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      <div className="font-medium text-red-800 mb-2">Reject this request</div>
      <Field label="Reason">
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="pp-input" data-testid="reject-reason">
          {REJECTION_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
      <Field label="Message to customer (optional, shown on Track page)"><textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="pp-input" data-testid="reject-message" /></Field>
      <Field label="Internal notes (private)"><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="pp-input" /></Field>
      <div className="pt-2 flex justify-end gap-2">
        <button onClick={onBack} className="btn-pill btn-ghost text-xs">Back</button>
        <button onClick={submit} disabled={saving} className="btn-pill text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" data-testid="reject-submit">{saving ? "Rejecting…" : "Confirm Reject"}</button>
      </div>
    </div>
  );
}

/* ===================== Product Modal (admin management) ===================== */
function ProductModal({ pid, onClose, onDone, allProducts }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [mergeInto, setMergeInto] = useState("");

  const load = async () => { try { const { data } = await api.get(`/admin/wishlist/products/${pid}`); setData(data); setForm(data.product); } catch (e) { toast.error(formatApiError(e)); onClose(); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);
  if (!data) return null;

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      ["name", "brand", "category", "variant", "size", "description", "public_status", "visibility", "admin_notes",
       "supplier_name", "supplier_contact", "estimated_cost", "expected_retail_price", "min_order_qty",
       "expected_delivery", "actual_received", "supplier_notes", "inventory_product_id"].forEach((k) => {
        if (form[k] !== data.product[k]) patch[k] = form[k];
      });
      if (Object.keys(patch).length === 0) { toast.info("No changes"); setSaving(false); return; }
      await api.put(`/admin/wishlist/products/${pid}`, patch);
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const merge = async () => {
    if (!mergeInto || mergeInto === pid) return toast.error("Pick a different target product");
    if (!window.confirm("Merge this product into the selected one? All requests and likes will move.")) return;
    try {
      await api.post("/admin/wishlist/products/merge", { into_product_id: mergeInto, from_product_id: pid });
      toast.success("Merged"); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <ModalShell title={data.product.name} subtitle={`${data.product.request_count} requests · ${data.product.like_count} likes`} onClose={onClose} wide testid="product-modal">
      <div className="flex gap-2 mb-4 text-xs">
        <StatusChip status={data.product.public_status} />
        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${data.product.visibility === "public" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{data.product.visibility}</span>
      </div>
      {data.product.image_path && <img src={`${API_BASE}/wishlist/images/${data.product.image_path}`} alt="" className="w-full max-h-48 object-cover rounded-xl mb-4 border border-brand-line" />}

      <SectionHdr>Public details</SectionHdr>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <Field label="Name"><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pp-input" /></Field>
        <Field label="Brand"><input value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="pp-input" /></Field>
        <Field label="Category">
          <select value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="pp-input">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Variant"><input value={form.variant || ""} onChange={(e) => setForm({ ...form, variant: e.target.value })} className="pp-input" /></Field>
        <Field label="Size"><input value={form.size || ""} onChange={(e) => setForm({ ...form, size: e.target.value })} className="pp-input" /></Field>
        <Field label="Public Status">
          <select value={form.public_status || ""} onChange={(e) => setForm({ ...form, public_status: e.target.value })} className="pp-input" data-testid="edit-public-status">
            {PUBLIC_STATUSES.map((s) => <option key={s} value={s}>{labelFor(s)}</option>)}
          </select>
        </Field>
        <Field label="Visibility">
          <select value={form.visibility || "public"} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="pp-input" data-testid="edit-visibility">
            <option value="public">Public</option>
            <option value="hidden">Hidden</option>
            <option value="archived">Archived</option>
          </select>
        </Field>
        <Field label="Inventory ID (optional)"><input value={form.inventory_product_id || ""} onChange={(e) => setForm({ ...form, inventory_product_id: e.target.value })} className="pp-input" placeholder="Link to inventory module" /></Field>
      </div>
      <Field label="Description"><textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="pp-input" /></Field>

      <SectionHdr>Procurement (private)</SectionHdr>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <Field label="Supplier name"><input value={form.supplier_name || ""} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="pp-input" /></Field>
        <Field label="Supplier contact"><input value={form.supplier_contact || ""} onChange={(e) => setForm({ ...form, supplier_contact: e.target.value })} className="pp-input" /></Field>
        <Field label="Estimated cost (₹)"><input type="number" step="0.01" value={form.estimated_cost || ""} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value ? parseFloat(e.target.value) : null })} className="pp-input" /></Field>
        <Field label="Expected retail (₹)"><input type="number" step="0.01" value={form.expected_retail_price || ""} onChange={(e) => setForm({ ...form, expected_retail_price: e.target.value ? parseFloat(e.target.value) : null })} className="pp-input" /></Field>
        <Field label="Min order qty"><input type="number" value={form.min_order_qty || ""} onChange={(e) => setForm({ ...form, min_order_qty: e.target.value ? parseInt(e.target.value) : null })} className="pp-input" /></Field>
        <Field label="Expected delivery"><input type="date" value={form.expected_delivery || ""} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} className="pp-input" /></Field>
        <Field label="Actual received"><input type="date" value={form.actual_received || ""} onChange={(e) => setForm({ ...form, actual_received: e.target.value })} className="pp-input" /></Field>
      </div>
      <Field label="Supplier notes (private)"><textarea rows={2} value={form.supplier_notes || ""} onChange={(e) => setForm({ ...form, supplier_notes: e.target.value })} className="pp-input" /></Field>
      <Field label="Admin notes (private)"><textarea rows={2} value={form.admin_notes || ""} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} className="pp-input" /></Field>

      <SectionHdr>Linked requests ({data.requests.length})</SectionHdr>
      <div className="max-h-48 overflow-y-auto rounded-xl border border-brand-line divide-y divide-brand-line/60 mb-5">
        {data.requests.length === 0 ? <div className="p-3 text-xs text-brand-charcoal/50">No linked requests.</div> : data.requests.map((r) => (
          <div key={r.public_ref} className="p-2.5 text-xs flex items-center justify-between">
            <span className="font-mono">{r.public_ref}</span>
            <span className="text-brand-charcoal/60 truncate mx-2 flex-1">{r.submitted_name}</span>
            <StatusChip status={r.status} />
          </div>
        ))}
      </div>

      <SectionHdr>Audit log</SectionHdr>
      <div className="max-h-32 overflow-y-auto rounded-xl border border-brand-line divide-y divide-brand-line/60 mb-5">
        {data.events.length === 0 ? <div className="p-3 text-xs text-brand-charcoal/50">No events yet.</div> : data.events.map((e, i) => (
          <div key={i} className="p-2 text-[11px] flex items-center justify-between">
            <span>{labelFor(e.action)}</span>
            <span className="text-brand-charcoal/55">{e.actor} · {new Date(e.at).toLocaleString()}</span>
          </div>
        ))}
      </div>

      {showMerge && (
        <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 mb-4">
          <div className="text-sm font-medium mb-2">Merge this product into another</div>
          <select value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} className="pp-input mb-2" data-testid="merge-into">
            <option value="">Select target product…</option>
            {allProducts.filter((x) => x.id !== pid).map((p) => <option key={p.id} value={p.id}>{p.name} ({p.request_count} reqs)</option>)}
          </select>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowMerge(false)} className="btn-pill btn-ghost text-xs">Cancel</button>
            <button onClick={merge} className="btn-pill text-xs bg-amber-600 text-white hover:bg-amber-700" data-testid="confirm-merge">Confirm Merge</button>
          </div>
        </div>
      )}

      <div className="pt-4 flex justify-between gap-2 flex-wrap sticky bottom-0 bg-white border-t border-brand-line mt-5 -mx-6 px-6 py-3">
        <button onClick={() => setShowMerge((s) => !s)} className="btn-pill btn-ghost text-xs" data-testid="btn-merge-mode"><Merge size={11} /> Merge</button>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-pill btn-ghost text-xs">Close</button>
          <button onClick={save} disabled={saving} className="btn-pill btn-dark text-xs disabled:opacity-50" data-testid="btn-save-product">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </ModalShell>
  );
}

function Field({ label, children }) { return (<div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55 block mb-1">{label}</label>{children}</div>); }
function SectionHdr({ children }) { return (<div className="text-[11px] uppercase tracking-widest text-brand-gold font-medium mb-2 mt-4 first:mt-0">{children}</div>); }
function ModalShell({ title, subtitle, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className={`${wide ? "max-w-3xl" : "max-w-xl"} w-full bg-white rounded-2xl p-6 max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-2xl truncate">{title}</div>
            {subtitle && <div className="text-xs text-brand-charcoal/55 mt-0.5">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="text-brand-charcoal/60 hover:text-brand-charcoal"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
